/**
 * The route outlet.
 *
 * M1's discriminated union is gone: magic links arrive as URLs, so navigation
 * had to become the URL (see lib/router.tsx). This file is the only place that
 * knows which route renders which screen, and the only place that decides
 * whether a route needs a session.
 */
import { useEffect } from 'react'

import { AppShell } from '@/components/AppShell'
import { LoadingBlock } from '@/components/LoadingBlock'
import { AdminScreen } from '@/features/admin/AdminScreen'
import { DossierAuditScreen } from '@/features/audit/DossierAuditScreen'
import { InviteScreen } from '@/features/auth/InviteScreen'
import { SignInScreen } from '@/features/auth/SignInScreen'
import {
  rememberWorkspace,
  recalledWorkspace,
  signInHref,
  useSession,
} from '@/features/auth/SessionContext'
import { FactsScreen } from '@/features/facts/FactsScreen'
import { PrintReport } from '@/features/report/PrintReport'
import { injectedPrintPayload } from '@/features/report/print'
import { ReportView } from '@/features/report/ReportView'
import { BzoIngestScreen } from '@/features/rulebase/BzoIngestScreen'
import { GemeindenOverview } from '@/features/rulebase/GemeindenOverview'
import { IngestionProgress } from '@/features/rulebase/IngestionProgress'
import { PackScreen } from '@/features/rulebase/PackScreen'
import { RunProgress } from '@/features/run/RunProgress'
import { UploadScreen } from '@/features/upload/UploadScreen'
import { WorkspaceIndex } from '@/features/workspace/WorkspaceIndex'
import { WorkspaceScreen } from '@/features/workspace/WorkspaceScreen'
import { useI18n } from '@/i18n'
import { ROUTES, Link, buildPath, currentHref, navigate, useRouteMatch } from '@/lib/router'
import type { WorkspaceRef } from '@/api/types'

export default function App() {
  const route = useRouteMatch()
  const { status, workspaces } = useSession()
  const workspaceId = route.params.workspaceId ?? null
  // The PDF export injects the report before the bundle boots. An injected
  // payload IS the instruction "render the print form" — no route, no session
  // and no network call are involved, so the exporter does not have to serve
  // the bundle under the print path to get the print page.
  const injected = injectedPrintPayload()

  // Keep the last workspace so `/` lands where the reader left off.
  useEffect(() => {
    if (workspaceId) rememberWorkspace(workspaceId)
  }, [workspaceId])

  // `/` is a redirect, not a screen: signed in it resolves to a workspace,
  // signed out it resolves to the sign-in.
  useEffect(() => {
    if (route.name !== 'home') return
    if (status === 'loading') return
    if (status === 'signedOut') {
      navigate(ROUTES.signIn, { replace: true })
      return
    }
    const recalled = recalledWorkspace()
    const target =
      workspaces.find((workspace) => workspace.id === recalled) ?? workspaces[0] ?? null
    navigate(
      target ? buildPath('workspace', { workspaceId: target.id }) : ROUTES.workspaceIndex,
      { replace: true },
    )
  }, [route.name, status, workspaces])

  if (injected) return <PrintReport injected={injected} />

  // `sessionRow={false}`: the two token surfaces are signed out by definition,
  // so the header's second row would stay empty forever. See AppShell.
  if (route.name === 'signIn') {
    return (
      <AppShell sessionRow={false}>
        <SignInScreen />
      </AppShell>
    )
  }

  if (route.name === 'invite') {
    return (
      <AppShell sessionRow={false}>
        <InviteScreen />
      </AppShell>
    )
  }

  // The print form renders outside the application shell — paper carries no
  // navigation. It still needs a session, because it reads the report through
  // the ordinary authenticated client.
  if (route.name === 'reportPrint' && route.params.runId) {
    if (status === 'loading') return null
    if (status === 'signedOut') {
      return (
        <AppShell>
          <SignInHint />
        </AppShell>
      )
    }
    return <PrintReport runId={route.params.runId} />
  }

  return (
    <AppShell activeWorkspaceId={workspaceId}>
      <Guarded status={status}>
        <Routed
          name={route.name}
          workspaceId={workspaceId}
          dossierId={route.params.dossierId ?? null}
          runId={route.params.runId ?? null}
          params={route.params}
          workspaces={workspaces}
        />
      </Guarded>
    </AppShell>
  )
}

/** Everything below the two token surfaces needs a session. */
function Guarded({ status, children }: { status: string; children: React.ReactNode }) {
  if (status === 'loading') return <Loading />
  if (status === 'signedOut') return <SignInHint />
  return <>{children}</>
}

function Loading() {
  // Height-reserving: the session gate stands in front of every content
  // surface, all of which are taller than a viewport. See LoadingBlock.
  return <LoadingBlock />
}

/**
 * The 401 handler has already pushed the sign-in route; this is only what the
 * frame shows in the frame or two before it lands.
 */
function SignInHint() {
  const { t } = useI18n()
  return (
    <p className="text-[13px] text-muted">
      <Link to={signInHref(currentHref())} className="underline underline-offset-2">
        {t('auth.title')}
      </Link>
    </p>
  )
}

function Routed({
  name,
  workspaceId,
  dossierId,
  runId,
  params,
  workspaces,
}: {
  name: string
  workspaceId: string | null
  dossierId: string | null
  runId: string | null
  params: Readonly<Record<string, string>>
  workspaces: WorkspaceRef[]
}) {
  const { t } = useI18n()
  const workspace = workspaceId
    ? (workspaces.find((candidate) => candidate.id === workspaceId) ?? null)
    : null

  if (name === 'workspaceIndex' || name === 'home') return <WorkspaceIndex />

  // The founder dashboard is not workspace-scoped: it is the view *across*
  // workspaces. Every route behind it answers 403 for a member, and the screen
  // itself says so rather than firing five requests that will all fail.
  if (name === 'admin') return <AdminScreen />

  if (name === 'facts' && workspace && dossierId) {
    return <FactsScreen workspace={workspace} dossierId={dossierId} />
  }

  if (name === 'dossierAudit' && workspace && dossierId) {
    return <DossierAuditScreen workspace={workspace} dossierId={dossierId} />
  }

  // M4 — the rulebase manager. Workspace-scoped like everything else that
  // touches workspace-owned data; a draft pack belongs to the Büro that
  // ingested it.
  if (workspace && name === 'rulebase') return <GemeindenOverview workspace={workspace} />
  if (workspace && name === 'rulebaseIngest') return <BzoIngestScreen workspace={workspace} />
  if (workspace && name === 'rulebaseIngestion' && params.ingestionId) {
    return <IngestionProgress workspace={workspace} ingestionId={params.ingestionId} />
  }
  if (workspace && name === 'rulebasePack' && params.kanton && params.gemeinde) {
    return (
      <PackScreen workspace={workspace} kanton={params.kanton} gemeinde={params.gemeinde} />
    )
  }

  if (
    name === 'workspace' ||
    name === 'upload' ||
    name === 'facts' ||
    name === 'dossierAudit' ||
    name === 'rulebase' ||
    name === 'rulebaseIngest' ||
    name === 'rulebaseIngestion' ||
    name === 'rulebasePack'
  ) {
    if (!workspace) {
      return (
        <div className="flex flex-col gap-3">
          <p role="alert" className="text-[15px] text-ink">
            {t('workspace.notFound')}
          </p>
          <Link to={ROUTES.workspaceIndex} className="text-[13px] underline underline-offset-2">
            {t('workspace.indexLink')}
          </Link>
        </div>
      )
    }
    return name === 'upload' ? (
      <UploadScreen workspace={workspace} />
    ) : (
      <WorkspaceScreen workspace={workspace} />
    )
  }

  if (name === 'run' && runId) {
    return (
      <RunProgress
        runId={runId}
        initialRun={null}
        onOpenReport={(id) => navigate(buildPath('report', { runId: id }))}
        onBack={() => navigate(ROUTES.home)}
      />
    )
  }

  if (name === 'report' && runId) {
    return (
      <ReportView
        runId={runId}
        onBack={() => navigate(buildPath('run', { runId }))}
        onNewRun={() => navigate(ROUTES.home)}
      />
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <h1 className="font-display text-[32px] font-semibold">{t('notFound.title')}</h1>
      <p className="text-[15px] text-muted">{t('notFound.lead')}</p>
      <Link to={ROUTES.home} className="text-[13px] underline underline-offset-2">
        {t('notFound.home')}
      </Link>
    </div>
  )
}
